// Line splitting helpers used by class-variable transformations.
// Example: splitTopLevelStatements("a;b(c;d);e") keeps nested `;` intact.
import type { StatementPart } from './types'

// Split source text into lines while preserving trailing empty line semantics.
export function splitLines(input: string): string[] {
  const lines: string[] = []
  let current = ''

  // Walk source and flush one line on each newline boundary.
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index]
    if (char === '\n') {
      lines.push(current)
      current = ''
      continue
    } else if (char === '\r') {
      lines.push(current)
      current = ''
      if (input[index + 1] === '\n') {
        index += 1
      } else {
        // Continue when CR is not followed by LF.
      }
      continue
    } else {
      // Keep accumulating current line content.
    }

    current += char
  }

  lines.push(current)
  // Return split lines array.
  return lines
}

// Split one line by top-level semicolons while preserving nested scopes.
export function splitTopLevelStatements(line: string): StatementPart[] {
  const parts: StatementPart[] = []
  let current = ''
  let quote: "'" | '"' | null = null
  let depthParen = 0
  let depthBrace = 0
  let depthBracket = 0

  // Scan line and split only on semicolons outside nested contexts.
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const previous = index > 0 ? line[index - 1] : ''

    // Keep quoted content opaque until matching quote closes.
    if (quote) {
      current += char
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Keep reading quoted text.
      }
      continue
    } else {
      // Continue scanning non-quoted characters.
    }

    // Enter quote mode when scanner sees `'` or `"`.
    if (char === "'" || char === '"') {
      quote = char
      current += char
      continue
    } else {
      // Continue depth tracking outside quotes.
    }

    // Track nested depths so only top-level semicolons split statements.
    if (char === '(') {
      depthParen += 1
    } else if (char === ')' && depthParen > 0) {
      depthParen -= 1
    } else if (char === '{') {
      depthBrace += 1
    } else if (char === '}' && depthBrace > 0) {
      depthBrace -= 1
    } else if (char === '[') {
      depthBracket += 1
    } else if (char === ']' && depthBracket > 0) {
      depthBracket -= 1
    } else {
      // Keep depth counters unchanged.
    }

    // Flush current part when scanner reaches top-level semicolon.
    if (char === ';' && depthParen === 0 && depthBrace === 0 && depthBracket === 0) {
      parts.push({
        text: current,
        hasTerminator: true,
      })
      current = ''
      continue
    } else {
      // Keep collecting statement text.
    }

    current += char
  }

  parts.push({
    text: current,
    hasTerminator: false,
  })
  // Return all split statement parts.
  return parts
}
